import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {Observable, catchError, finalize, map, of, shareReplay, switchMap, tap} from 'rxjs';
import {ManagementService} from './management.service';
import {
  Account,
  AccountPayload,
  AccountSummary,
  Brand,
  BrandPayload,
  BrandSummary,
  ManagementRequest,
  SortState,
  Topic,
  User,
  UserPayload,
} from './types';

export type TableRow = Account | User | Brand;

interface TableQuery {
  tab: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  term: string;
}

interface TableResult {
  rows: TableRow[];
  total: number;
}

export type MetaPageCountry = { code: string; name: string };

const PAGE_SIZE: Record<string, number> = {accounts: 10, users: 10, brands: 15};

/**
 * Single source of truth for the management screen. Owns:
 *  - the active table's query state (tab/page/sort/term) and its rows/total/loading/error,
 *  - cross-tab reference data for the drawers' dropdowns, lazily fetched once and cached for the
 *    session (so revisiting the screen or reopening a drawer doesn't refetch),
 *  - every list mutation, each of which does exactly one refresh + the matching cache invalidation.
 *
 * Loading is a LOCAL signal here — the management screen no longer touches the global StateService
 * spinner, so there is no cross-component counter to keep balanced.
 */
@Injectable({providedIn: 'root'})
export class ManagementStore {
  private readonly api = inject(ManagementService);
  private readonly destroyRef = inject(DestroyRef);

  // --- query state ---
  private readonly _tab = signal('');
  private readonly _page = signal(0);
  private readonly _sortBy = signal('');
  private readonly _sortDir = signal<'asc' | 'desc'>('desc');
  private readonly _term = signal('');
  private readonly _reloadTrigger = signal(0);

  // --- table view state ---
  private readonly _rows = signal<TableRow[]>([]);
  private readonly _total = signal(0);
  private readonly _loading = signal(false);
  private readonly _error = signal(false);

  readonly activeTab = this._tab.asReadonly();
  readonly rows = this._rows.asReadonly();
  readonly total = this._total.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly page = this._page.asReadonly();
  readonly term = this._term.asReadonly();
  readonly pageSize = computed(() => PAGE_SIZE[this._tab()] ?? PAGE_SIZE['accounts']);

  private readonly query = computed<TableQuery>(() => {
    this._reloadTrigger(); // re-run the fetch on an explicit reload (e.g. after a mutation), even if params are unchanged
    return {
      tab: this._tab(),
      page: this._page(),
      pageSize: this.pageSize(),
      sortBy: this._sortBy(),
      sortDir: this._sortDir(),
      term: this._term(),
    };
  });

  // --- reference data (lazy + cached) ---
  private readonly _brandSummaries = signal<BrandSummary[]>([]);
  private readonly _accountSummaries = signal<AccountSummary[]>([]);
  private readonly _roles = signal<string[]>([]);
  private readonly _aiTopics = signal<Topic[]>([]);

  readonly brandSummaries = this._brandSummaries.asReadonly();
  readonly accountSummaries = this._accountSummaries.asReadonly();
  readonly roles = this._roles.asReadonly();
  readonly aiTopics = this._aiTopics.asReadonly();

  private brandSummaries$?: Observable<BrandSummary[]>;
  private accountSummaries$?: Observable<AccountSummary[]>;
  private roles$?: Observable<string[]>;
  private aiTopics$?: Observable<Topic[]>;
  private metaPageCountries$?: Observable<MetaPageCountry[]>;

  constructor() {
    // One cancelable load pipeline. switchMap cancels a stale request when the query changes;
    // finalize keeps the loading flag balanced even on cancellation; catchError surfaces the
    // failure without tearing down the stream, so retries keep working.
    toObservable(this.query)
      .pipe(
        switchMap(query => {
          if (!query.tab) {
            return of(null);
          }
          this._loading.set(true);
          this._error.set(false);
          return this.fetchFor(query).pipe(
            // No logging here — ManagementService already logs every failed request before rethrowing.
            catchError(() => {
              this._error.set(true);
              return of(null);
            }),
            finalize(() => this._loading.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        if (result) {
          this._rows.set(result.rows);
          this._total.set(result.total);
        }
      });
  }

  // --- query actions (called by the screen presenter) ---

  setTab(tab: string): void {
    if (tab === this._tab()) {
      return;
    }
    this._tab.set(tab);
    this.resetTabState();
    // Claim loading synchronously so the empty-state overlay can't flash between rows being cleared
    // and the (async) fetch pipeline flipping the flag. finalize() will clear it when the fetch settles.
    this._loading.set(true);
  }

  setPage(pageIndex: number): void {
    this._page.set(pageIndex);
  }

  setSort(sort: SortState): void {
    this._sortBy.set(sort.sortBy);
    this._sortDir.set(sort.sortDir);
  }

  setSearch(term: string): void {
    this._page.set(0);
    this._term.set(term ?? '');
  }

  reload(): void {
    this._reloadTrigger.update(value => value + 1);
  }

  private resetTabState(): void {
    this._page.set(0);
    this._sortBy.set('');
    this._sortDir.set('desc');
    this._term.set('');
    // Clear immediately so the newly-shown table never renders the previous tab's rows (different
    // shape) against its columns. Page/sort/search within a tab keep the rows for a smooth update.
    this._rows.set([]);
    this._total.set(0);
    this._error.set(false);
  }

  private fetchFor(query: TableQuery): Observable<TableResult> {
    const params: Partial<ManagementRequest> = {
      limit: query.pageSize,
      offset: query.page * query.pageSize,
      term: query.term,
      sortBy: query.sortBy,
      sortDir: query.sortDir,
    };
    switch (query.tab) {
      case 'users':
        return this.api.getUsers(params).pipe(map(res => ({rows: res.users as TableRow[], total: res.total})));
      case 'brands':
        return this.api.getBrands({...params, elaborate: true}).pipe(map(res => ({rows: res.brands as TableRow[], total: res.total})));
      case 'accounts':
        return this.api.getAccounts({...params, elaborate: true}).pipe(map(res => ({rows: res.accounts as TableRow[], total: res.total})));
      default:
        return of({rows: [], total: 0});
    }
  }

  private replaceRow(match: (row: TableRow) => boolean, updated: TableRow): void {
    const rows = this._rows();
    const index = rows.findIndex(match);
    if (index === -1) {
      return;
    }
    const next = [...rows];
    next[index] = updated;
    this._rows.set(next);
  }

  // --- reference data loaders ---
  // Each slice caches its (shared) HTTP observable so concurrent callers dedupe and a second drawer
  // open hits the cache. refresh*() busts the cache after a mutation that can change the list.

  ensureBrandSummaries(): void {
    this.brandSummaries$ ??= this.api.getBrands({elaborate: false}).pipe(
      map(res => res.brands as BrandSummary[]),
      catchError(() => of([] as BrandSummary[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.brandSummaries$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this._brandSummaries.set(list));
  }

  // Bust the cache only — the list is re-fetched lazily the next time a drawer needs it, rather than
  // eagerly here (nothing is reading it while no drawer is open).
  private invalidateBrandSummaries(): void {
    this.brandSummaries$ = undefined;
  }

  ensureAccountSummaries(): void {
    this.accountSummaries$ ??= this.api.getAccounts({elaborate: false}).pipe(
      map(res => res.accounts as AccountSummary[]),
      catchError(() => of([] as AccountSummary[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.accountSummaries$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this._accountSummaries.set(list));
  }

  private invalidateAccountSummaries(): void {
    this.accountSummaries$ = undefined;
  }

  ensureRoles(): void {
    this.roles$ ??= this.api.getUserRoles().pipe(
      map(res => res.roles),
      catchError(() => of([] as string[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.roles$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this._roles.set(list));
  }

  ensureAiTopics(): void {
    this.aiTopics$ ??= this.api.getAiTopics().pipe(
      map(res => res.topics),
      catchError(() => of([] as Topic[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    this.aiTopics$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this._aiTopics.set(list));
  }

  // Static, brand-scoped list — cached for the session and shared across brand-drawer opens. Returned
  // as an observable (rather than a signal) because each consumer maps it differently (e.g. flag URLs).
  getMetaPageCountries(): Observable<MetaPageCountry[]> {
    this.metaPageCountries$ ??= this.api.getMetaPageCountries().pipe(
      catchError(() => of([] as MetaPageCountry[])),
      shareReplay({bufferSize: 1, refCount: false}),
    );
    return this.metaPageCountries$;
  }

  // --- mutations (the only place list side-effects live) ---
  // Each returns the repo result so the drawer can show its snackbar / close; the tap performs the
  // single list refresh + the matching reference-cache invalidation.

  addBrand(payload: BrandPayload): Observable<Brand> {
    return this.api.addBrand(payload).pipe(
      tap(result => {
        // Filter the table to the just-added brand (matches the previous UX) and refresh the
        // competitor dropdown's source.
        this._term.set(result.name);
        this._page.set(0);
        this.reload();
        this.invalidateBrandSummaries();
      }),
    );
  }

  editBrand(payload: BrandPayload): Observable<Brand> {
    return this.api.editBrand(payload).pipe(
      tap(result => {
        this.replaceRow(row => (row as Brand).brandKey === result.brandKey, result);
        this.invalidateBrandSummaries(); // name can change
      }),
    );
  }

  addAccount(payload: AccountPayload): Observable<Account> {
    return this.api.addAccount(payload).pipe(
      tap(() => {
        this._term.set('');
        this._page.set(0);
        this.reload();
        this.invalidateAccountSummaries();
      }),
    );
  }

  editAccount(payload: AccountPayload): Observable<Account> {
    return this.api.editAccount(payload).pipe(
      tap(result => {
        this.replaceRow(row => (row as Account).accountKey === result.accountKey, result);
        this.invalidateAccountSummaries(); // name / brands feed the user drawer's account dropdown
      }),
    );
  }

  deleteAccount(accountKey: string): Observable<Account> {
    return this.api.deleteAccount(accountKey).pipe(
      tap(() => {
        this.reload();
        this.invalidateAccountSummaries();
      }),
    );
  }

  addUser(payload: UserPayload): Observable<User> {
    return this.api.addUser(payload).pipe(
      tap(() => {
        this._term.set('');
        this._page.set(0);
        this.reload();
      }),
    );
  }

  editUser(payload: UserPayload): Observable<User> {
    return this.api.editUser(payload).pipe(
      tap(result => {
        this.replaceRow(row => (row as User).id === result.id, result);
      }),
    );
  }

  deleteUser(userId: string): Observable<unknown> {
    return this.api.deleteUser(userId).pipe(
      tap(() => {
        this.reload();
      }),
    );
  }
}
